import { Routes, Route } from 'react-router-dom';
import { Box } from '@chakra-ui/react';
import { SignUpPage } from './features/auth/pages/SignUpPage';
import { OAuthCallbackPage } from './features/auth/pages/OAuthCallbackPage';
import { IdpCallbackPage } from './features/auth/pages/IdpCallbackPage';
import { SettingsPage } from './features/settings/pages/SettingsPage';
import { OrganizationSettingsPage } from './features/organization/pages/OrganizationSettingsPage';
import { LocationsPage } from './features/organization/pages/LocationsPage';
import { UsersPage } from './features/organization/pages/UsersPage';
import { AuditLogPage } from './features/organization/pages/AuditLogPage';
import { OnboardingCarouselPage } from './pages/onboarding/index';
import { WizardPage } from './pages/onboarding/wizard';
import { SubscriptionCallbackPage } from './pages/onboarding/subscription-callback';
import { PlayerPage } from './features/player/pages/PlayerPage';
import { LandingPage } from './features/landing/LandingPage';
import { LegalPage } from './features/landing/LegalPage';
import { ContactsPage } from './features/landing/ContactsPage';
import { ProtectedLayout } from './layouts/ProtectedLayout';
import { DashboardPage } from './pages/DashboardPage';
import { PlaylistsPage } from './features/media-library/pages/PlaylistsPage';
import { PlaylistDetailPage } from './features/media-library/pages/PlaylistDetailPage';
import { CustomContentPage } from './features/media-library/pages/CustomContentPage';
import { TrashPage } from './features/media-library/pages/TrashPage';
import { AiGenerationPage } from './features/ai-generation/pages/AiGenerationPage';
import { ScenariosPage } from './features/scenarios/pages/ScenariosPage';
import { ScenarioDetailPage } from './features/scenarios/pages/ScenarioDetailPage';
import { SchedulePage } from './features/schedule/pages/SchedulePage';
import { PlaybackReportPage } from './features/schedule/pages/PlaybackReportPage';
import { AlertsPage } from './features/alerts/pages/AlertsPage';
import { SubscriptionPage } from './features/subscription/pages/SubscriptionPage';
import { BillingReturnPage } from './features/subscription/pages/BillingReturnPage';
import { LocationSchedulePage } from './features/locations/pages/LocationSchedulePage';
import { InvitationAcceptPage } from './features/organization/pages/InvitationAcceptPage';
import { RequireAuth } from './features/auth/RequireAuth';

export default function App() {
  return (
    <Box minH="100vh" bg="white">
      <Routes>
        {/* Public marketing + legal — no auth, no app shell */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/terms" element={<LegalPage kind="terms" />} />
        <Route path="/privacy" element={<LegalPage kind="privacy" />} />
        <Route path="/contacts" element={<ContactsPage />} />

        {/* Onboarding — full-screen, no app shell */}
        <Route path="/onboarding" element={<OnboardingCarouselPage />} />
        <Route path="/onboarding/wizard" element={<WizardPage />} />
        <Route path="/onboarding/subscription-success" element={<SubscriptionCallbackPage />} />

        {/* Auth entry points */}
        <Route path="/auth/callback" element={<OAuthCallbackPage />} />
        <Route path="/auth/idp-callback" element={<IdpCallbackPage />} />
        <Route path="/signup" element={<SignUpPage />} />

        {/* Billing checkout return — full-screen, provider-agnostic (polls status). Needs auth. */}
        <Route
          path="/billing/success"
          element={
            <RequireAuth>
              <BillingReturnPage outcome="success" />
            </RequireAuth>
          }
        />
        <Route
          path="/billing/cancel"
          element={
            <RequireAuth>
              <BillingReturnPage outcome="cancel" />
            </RequireAuth>
          }
        />

        {/* Invitation accept — needs auth, but bypasses the onboarding shell. */}
        <Route
          path="/invitations/accept"
          element={
            <RequireAuth>
              <InvitationAcceptPage />
            </RequireAuth>
          }
        />

        {/* Authenticated app — RequireAuth + OnboardingGuard + app shell (sidebar/topbar) */}
        <Route element={<ProtectedLayout />}>
          <Route path="/app" element={<DashboardPage />} />
          <Route path="/library" element={<PlaylistsPage />} />
          <Route path="/library/clips" element={<CustomContentPage />} />
          <Route path="/library/trash" element={<TrashPage />} />
          <Route path="/library/ai" element={<AiGenerationPage />} />
          <Route path="/library/playlists/:id" element={<PlaylistDetailPage />} />
          <Route path="/scenarios" element={<ScenariosPage />} />
          <Route path="/scenarios/:id" element={<ScenarioDetailPage />} />
          <Route path="/schedule" element={<SchedulePage />} />
          <Route path="/schedule/report" element={<PlaybackReportPage />} />
          <Route path="/alerts" element={<AlertsPage />} />
          <Route path="/player" element={<PlayerPage />} />
          <Route path="/player/:locationId" element={<PlayerPage />} />
          <Route path="/organization/settings" element={<OrganizationSettingsPage />} />
          <Route path="/organization/locations" element={<LocationsPage />} />
          <Route path="/organization/locations/:locationId/schedule" element={<LocationSchedulePage />} />
          <Route path="/organization/users" element={<UsersPage />} />
          <Route path="/organization/audit-log" element={<AuditLogPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/subscription" element={<SubscriptionPage />} />
        </Route>
      </Routes>
    </Box>
  );
}
